import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtApiError } from "@/lib/adminApi";
import { Badge, EmptyState, inputCls, SUBJECT_OPTIONS } from "./shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CLASS_STATUSES = ["scheduled", "completed", "cancelled"];
const empty = { student_id: "", teacher_id: "", subject: "", date: new Date().toISOString().slice(0, 10), time: "17:00", duration: "60 min", recurring: "none", status: "scheduled" };

export default function AdminClasses() {
    const [items, setItems] = useState(null);
    const [students, setStudents] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/classes", { params: { status: statusFilter } }).then((r) => setItems(r.data.items)).catch(() => toast.error("Could not load classes"));
    useEffect(() => { load(); }, [statusFilter]);
    useEffect(() => {
        api.get("/students", { params: { status: "active" } }).then((r) => setStudents(r.data.items));
        api.get("/teachers").then((r) => setTeachers(r.data.items));
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/classes", { ...form, teacher_id: form.teacher_id || null });
            toast.success("Class scheduled");
            setOpen(false);
            setForm(empty);
            load();
        } catch (err) {
            toast.error(fmtApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const setStatus = async (id, status) => {
        await api.patch(`/classes/${id}`, { status });
        toast.success(`Class marked ${status}`);
        load();
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this class?")) return;
        await api.delete(`/classes/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="admin-classes">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-serif text-3xl font-semibold text-ink">Classes</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Schedule and track every lesson</p>
                </div>
                <button onClick={() => setOpen(true)} data-testid="add-class-button"
                    className="flex items-center gap-2 rounded-full bg-saffron px-6 py-2.5 text-sm font-medium text-cream transition-colors hover:bg-teal">
                    <Plus className="h-4 w-4" /> Schedule Class
                </button>
            </div>

            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className={`${inputCls} w-48`} data-testid="classes-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All classes</SelectItem>
                    {CLASS_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {!items || items.length === 0 ? (
                <EmptyState message="No classes yet. Click “Schedule Class” to add the first one." />
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-border bg-white card-shadow">
                    <table className="w-full text-sm" data-testid="classes-table">
                        <thead>
                            <tr className="border-b border-border text-left text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                <th className="px-5 py-4 font-medium">Student</th>
                                <th className="px-5 py-4 font-medium">Subject</th>
                                <th className="px-5 py-4 font-medium">Teacher</th>
                                <th className="px-5 py-4 font-medium">Date & Time</th>
                                <th className="px-5 py-4 font-medium">Duration</th>
                                <th className="px-5 py-4 font-medium">Repeats</th>
                                <th className="px-5 py-4 font-medium">Status</th>
                                <th className="px-5 py-4 font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="border-b border-border/60 last:border-0" data-testid={`class-row-${c.id}`}>
                                    <td className="px-5 py-4 font-medium text-ink">{c.student_name}</td>
                                    <td className="px-5 py-4">{c.subject}</td>
                                    <td className="px-5 py-4">{c.teacher_name || "—"}</td>
                                    <td className="px-5 py-4">{c.date} · {c.time}</td>
                                    <td className="px-5 py-4">{c.duration}</td>
                                    <td className="px-5 py-4 text-xs text-muted-foreground">{c.recurring === "none" ? "One-off" : c.recurring}</td>
                                    <td className="px-5 py-4">
                                        <Select value={c.status} onValueChange={(v) => setStatus(c.id, v)}>
                                            <SelectTrigger className="h-9 w-36 bg-white text-xs" data-testid={`class-status-${c.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {CLASS_STATUSES.map((s) => (
                                                    <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="px-5 py-4">
                                        <button onClick={() => remove(c.id)} className="text-muted-foreground transition-colors hover:text-red-600" data-testid={`class-delete-${c.id}`} aria-label="Delete class">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="class-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Schedule a Class</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="mt-2 space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-1.5">
                                <Label>Student *</Label>
                                <Select value={form.student_id} onValueChange={set("student_id")} required>
                                    <SelectTrigger className={inputCls} data-testid="class-student-select"><SelectValue placeholder="Choose student" /></SelectTrigger>
                                    <SelectContent>
                                        {students.map((s) => (
                                            <SelectItem key={s.id} value={s.id}>{s.student_name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Teacher</Label>
                                <Select value={form.teacher_id} onValueChange={set("teacher_id")}>
                                    <SelectTrigger className={inputCls} data-testid="class-teacher-select"><SelectValue placeholder="Choose teacher" /></SelectTrigger>
                                    <SelectContent>
                                        {teachers.map((t) => (
                                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Subject *</Label>
                                <Select value={form.subject} onValueChange={set("subject")} required>
                                    <SelectTrigger className={inputCls} data-testid="class-subject-select"><SelectValue placeholder="Choose subject" /></SelectTrigger>
                                    <SelectContent>
                                        {SUBJECT_OPTIONS.map((s) => (
                                            <SelectItem key={s} value={s}>{s}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Repeats</Label>
                                <Select value={form.recurring} onValueChange={set("recurring")}>
                                    <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">One-off</SelectItem>
                                        <SelectItem value="weekly">Weekly</SelectItem>
                                        <SelectItem value="twice a week">Twice a week</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Date *</Label>
                                <Input type="date" required value={form.date} onChange={set("date")} className={inputCls} data-testid="class-date-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Time *</Label>
                                <Input type="time" required value={form.time} onChange={set("time")} className={inputCls} data-testid="class-time-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Duration</Label>
                                <Input value={form.duration} onChange={set("duration")} className={inputCls} placeholder="60 min" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3">
                            <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-border px-6 py-2.5 text-sm font-medium">Cancel</button>
                            <button type="submit" disabled={saving} className="rounded-full bg-saffron px-6 py-2.5 text-sm font-medium text-cream hover:bg-teal disabled:opacity-60" data-testid="class-save-button">
                                {saving ? "Saving…" : "Schedule"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
